"use client"

import { useSubscription } from "@/api/subscription"
import { ManageSubscriptionButton } from "./ManageSubscriptionButton"
import { PurchaseSubscriptionButton } from "./PurchaseSubscriptionButton"
import { Button } from "@/components/ui/button"
import { ErrorCause } from "@/api/default-query"
import { useTeamUsers } from "@/api/teams"
import { usePathname } from "next/navigation"
import { ResourceUsage, SubscriptionPlan, SubscriptionTier } from "@/types/subscription"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card"
import { Check } from "lucide-react"
import { Team } from "@/types/team"
import pluralize from "pluralize"
import { Badge } from "@/components/ui/badge"
import { CircularProgress } from "@/components/icons/circular.progress"

const PLAN_ORDER = ["hobby_monthly", "professional_monthly"]
const planSort = (a: string, b: string) => {
  return PLAN_ORDER.indexOf(a) - PLAN_ORDER.indexOf(b)
}
const TIER_ORDER = ["hobby", "professional", "enterprise"]

const PLAN_TO_TIER: Record<SubscriptionPlan, SubscriptionTier> = {
  hobby_monthly: "hobby",
  hobby_yearly: "hobby",
  professional_monthly: "professional",
  professional_yearly: "professional",
}

type UpgradeOptions = {
  oldTier: SubscriptionTier
  newTier: SubscriptionTier
  seatLimit: number
}

const planUpgradeOptions = (
  currentTier: SubscriptionTier,
  currentSeats: number,
  currentUsers: number,
): UpgradeOptions | null => {
  let currentTierSeatLimit = null
  let upgradeTier: SubscriptionTier | null = null

  if (currentTier === "hobby") {
    if (currentSeats >= 10 || currentUsers > 10) {
      currentTierSeatLimit = 10
      upgradeTier = "professional"
    }
  }

  if (currentTier === "professional") {
    if (currentSeats >= 100 || currentUsers > 100) {
      currentTierSeatLimit = 100
      upgradeTier = "enterprise"
    }
  }

  let upgrade: UpgradeOptions | null = null
  if (upgradeTier && currentTierSeatLimit) {
    upgrade = {
      oldTier: currentTier,
      newTier: upgradeTier,
      seatLimit: currentTierSeatLimit,
    }
  }
  return upgrade
}

const planPurchaseOptions = (users: number) => {
  const plans: SubscriptionPlan[] = []

  if (users <= 100) {
    plans.push("professional_monthly")
  }

  if (users <= 10) {
    plans.push("hobby_monthly")
  }

  return plans.sort(planSort)
}

const nonSeatResourceExhausted = (resourceUsage: ResourceUsage[]) =>
  resourceUsage.some(
    ({ resource, usage, limit }) =>
      limit !== -1 && limit > 0 && resource !== "seat" && usage >= limit,
  )

const renderUsage = (usage: number, limit: number, resource: string) => {
  if (limit === -1) {
    return (
      <>
        <strong>{usage}</strong> {pluralize(resource, usage)} (Unlimited)
      </>
    )
  }

  return (
    <>
      <strong>
        {usage}/{limit}
      </strong>{" "}
      {resource}s
    </>
  )
}

const renderResourceUsage = (resourceUsage: ResourceUsage[], tier: SubscriptionTier | "free") => {
  if (resourceUsage.every(({ limit }) => limit === 0)) {
    return null
  }

  if (resourceUsage.length === 1) {
    const { usage, limit, resource } = resourceUsage[0]
    return (
      <>
        You have used <span className="capitalize">{renderUsage(usage, limit, resource)}</span>{" "}
        allowed on your <strong className="capitalize">{tier}</strong> plan.
      </>
    )
  }

  const filteredUsage = resourceUsage.filter(
    ({ limit, resource }) => limit !== 0 && resource !== "chatThink",
  )

  return (
    <div className="flex gap-2">
      {filteredUsage.map(({ resource, usage, limit }) => (
        <Card key={resource} className="min-w-48">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg capitalize">
              {resource}
              <CircularProgress
                current={usage}
                size={25}
                total={usage > limit ? usage : limit}
                className={limit === -1 ? "stroke-violet-500" : "stroke-blue-500"}
              />
            </CardTitle>
            <div className="flex items-center">
              <div className="text-sm capitalize">{renderUsage(usage, limit, resource)}</div>
            </div>
          </CardHeader>
        </Card>
      ))}
    </div>
  )
}

const renderManageSubscriptionText = (
  isNonSeatResourceExhausted: boolean,
  tier: SubscriptionTier,
  upgrade: UpgradeOptions | null,
) => {
  const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)
  if (isNonSeatResourceExhausted) {
    return `Manage subscription or upgrade to ${capitalize(TIER_ORDER[TIER_ORDER.indexOf(tier) + 1])}`
  } else if (upgrade) {
    return upgrade.newTier === "enterprise"
      ? "Contact us"
      : `Manage subscription or upgrade to ${capitalize(upgrade.newTier)}`
  } else {
    return "Manage subscription"
  }
}

const calculateRemainingPeriodDays = (trialEnd: string) => {
  const endDate = new Date(trialEnd)
  const now = new Date()
  const msInOneDay = 1000 * 60 * 60 * 24
  const remainingMs = endDate.getTime() - now.getTime()
  const daysLeft = Math.max(0, Math.floor(remainingMs / msInOneDay))
  return daysLeft
}

export const SubscriptionButtons = ({
  team,
  resourceUsage,
}: {
  team: Team
  resourceUsage: ResourceUsage[]
}) => {
  const teamId = team.id
  const teamSlug = team.slug
  const users = useTeamUsers(teamId)
  const subscription = useSubscription(teamId)
  const path = usePathname()
  const successUrl = `${window.location.origin}${path}`

  if (subscription.isPending || users.isPending) {
    return (
      <Button className="my-2" variant="default" type="button" disabled>
        Loading subscription...
      </Button>
    )
  }

  const subscriptionNotFound =
    subscription.isError && (subscription.error.cause as ErrorCause).status === 404

  // test not found case
  if ((!subscription.isSuccess && !subscriptionNotFound) || !users.isSuccess) {
    return (
      <Button className="my-2" variant="default" type="button" disabled>
        Failed to load subscription
      </Button>
    )
  }

  const activeUserCount = users.data.filter((user) => user.isActive).length

  if (
    subscriptionNotFound ||
    !subscription?.data ||
    subscription?.data?.stripeStatus === "canceled"
  ) {
    const plans = planPurchaseOptions(activeUserCount)

    const purchaseComponent = (plan: SubscriptionPlan) => (
      <>
        <PurchaseSubscriptionButton
          teamId={teamId}
          teamSlug={teamSlug}
          plan={plan}
          successUrl={successUrl}
        >
          Subscribe to&nbsp;<span className="capitalize"> {PLAN_TO_TIER[plan]}</span>&nbsp;plan with{" "}
          {pluralize("seat", activeUserCount, true)}
        </PurchaseSubscriptionButton>
        {/* <PurchaseTasksButton teamId={teamId} teamSlug={teamSlug} successUrl={successUrl}>
          Buy more tasks
        </PurchaseTasksButton> */}
      </>
    )

    const subscriptionEndDate = subscription.data?.endedAt
      ? new Date(subscription.data?.endedAt)
      : undefined
    const subscriptionEndingInFuture = subscriptionEndDate && subscriptionEndDate > new Date()

    return (
      <div>
        <div className="mb-6 mt-2">
          <p className="pb-4 text-sm font-semibold text-rose-600">
            {subscriptionEndDate && (
              <>
                {subscriptionEndingInFuture
                  ? "Your subscription will end on"
                  : "Your subscription ended on"}{" "}
                {subscriptionEndDate.toLocaleDateString("en-GB", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </>
            )}
          </p>
          {renderResourceUsage(resourceUsage, "free")}
        </div>

        <div>
          <Tabs defaultValue={PLAN_TO_TIER[plans[0]]} className="mx-auto w-full max-w-4xl">
            <TabsList className="mb-8 flex">
              {plans.map((plan) => (
                <TabsTrigger key={plan} value={PLAN_TO_TIER[plan]} className="w-full capitalize">
                  {PLAN_TO_TIER[plan]}
                </TabsTrigger>
              ))}
              <TabsTrigger value="enterprise" className="w-full">
                Enterprise
              </TabsTrigger>
            </TabsList>
            <TabsContent value="hobby">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Hobby</CardTitle>
                  <CardDescription>For personal projects</CardDescription>
                  <div>
                    <span className="text-4xl font-bold">$20</span>
                    <span className="ml-1 text-muted-foreground">/ seat / month</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <Check className="mr-2 size-4 text-primary" />
                      <span>15 tasks per seat</span>
                    </li>
                    <li className="flex items-center">
                      <Check className="mr-2 size-4 text-primary" />
                      <span>4 projects</span>
                    </li>
                    <li className="flex items-center">
                      <Check className="mr-2 size-4 text-primary" />
                      <span>Unlimited chats</span>
                    </li>
                    <li className="flex items-center">
                      <Check className="mr-2 size-4 text-primary" />
                      <span>Up to 10 seats</span>
                    </li>
                  </ul>
                </CardContent>
                <CardFooter>{purchaseComponent("hobby_monthly")}</CardFooter>
              </Card>
            </TabsContent>
            <TabsContent value="professional">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Professional</CardTitle>
                  <CardDescription>For teams</CardDescription>
                  <div>
                    <span className="text-4xl font-bold">$100</span>
                    <span className="ml-1 text-muted-foreground">/ seat / month</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <Check className="mr-2 size-4 text-primary" />
                      <span>120 tasks per seat</span>
                    </li>
                    <li className="flex items-center">
                      <Check className="mr-2 size-4 text-primary" />
                      <span>Unlimited projects</span>
                    </li>
                    <li className="flex items-center">
                      <Check className="mr-2 size-4 text-primary" />
                      <span>Unlimited chats</span>
                    </li>
                    <li className="flex items-center">
                      <Check className="mr-2 size-4 text-primary" />
                      <span>Up to 100 seats</span>
                    </li>
                  </ul>
                </CardContent>
                <CardFooter>{purchaseComponent("professional_monthly")}</CardFooter>
              </Card>
            </TabsContent>
            <TabsContent value="enterprise">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Enterprise</CardTitle>
                  <CardDescription>For large organizations</CardDescription>
                  <div>
                    <span className="text-4xl font-bold">Contact us</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <Check className="mr-2 size-4 text-primary" />
                      <span>Unlimited tasks</span>
                    </li>
                    <li className="flex items-center">
                      <Check className="mr-2 size-4 text-primary" />
                      <span>Unlimited projects</span>
                    </li>
                    <li className="flex items-center">
                      <Check className="mr-2 size-4 text-primary" />
                      <span>Unlimited chats</span>
                    </li>
                    <li className="flex items-center">
                      <Check className="mr-2 size-4 text-primary" />
                      <span>Unlimited seats</span>
                    </li>
                    <li className="flex items-center">
                      <Check className="mr-2 size-4 text-primary" />
                      <span>Custom models</span>
                    </li>
                  </ul>
                </CardContent>
                <CardFooter>
                  <a
                    href="mailto:support@cosine.sh?subject=Enterprise%20Subscription"
                    className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                  >
                    Contact us
                  </a>
                </CardFooter>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
        {subscription.data?.stripeCustomerId && (
          <div className="flex w-fit pt-6">
            <ManageSubscriptionButton
              teamId={teamId}
              teamSlug={teamSlug}
              stripeCustomerId={subscription.data.stripeCustomerId}
              successUrl={successUrl}
            >
              Manage Subscription
            </ManageSubscriptionButton>
          </div>
        )}
      </div>
    )
  }

  const upgrade = planUpgradeOptions(
    subscription.data.tier,
    subscription.data.quantity,
    activeUserCount,
  )

  const isNonSeatResourceExhausted = nonSeatResourceExhausted(resourceUsage)

  const subscriptionToStatus: Record<string, string> = {
    active: "active",
    canceled: "cancelled",
    trialing: "free trial",
    incomplete: "incomplete",
    incomplete_expired: "incomplete",
    paused: "paused",
    past_due: "past due",
    unpaid: "unpaid",
  }

  const subscriptionToVariant: Record<string, "default" | "secondary" | "destructive" | "outline"> =
    {
      active: "default",
      canceled: "destructive",
      trialing: "secondary",
      incomplete: "default",
      incomplete_expired: "secondary",
      paused: "secondary",
      past_due: "destructive",
      unpaid: "destructive",
    }

  return (
    <div>
      <Card>
        <CardHeader>
          <span className="flex gap-2">
            <CardTitle className="capitalize">{subscription.data.tier} plan</CardTitle>
            <Badge
              variant={subscriptionToVariant[subscription.data.stripeStatus]}
              className="capitalize"
            >
              {subscriptionToStatus[subscription.data.stripeStatus]}
            </Badge>
            {(subscription.data.trialEnd || subscription.data.cancelAtPeriodEnd) && (
              <Badge variant="outline">
                {subscription.data.cancelAtPeriodEnd ? "Expiring in" : "Ending in"}{" "}
                {pluralize(
                  "day",
                  calculateRemainingPeriodDays(
                    subscription.data.currentPeriodEnd ?? subscription.data.trialEnd,
                  ),
                  true,
                )}
              </Badge>
            )}
          </span>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <strong>
              $
              {subscription.data.price ? subscription.data.price * subscription.data.quantity : "-"}
              /mo
            </strong>{" "}
            (x
            {pluralize("seat", subscription.data.quantity, true)})
            <CardDescription>
              Current billing cycle&nbsp;&nbsp;&nbsp;
              {new Date(subscription.data.currentPeriodStart).toLocaleDateString("en-GB", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}{" "}
              to{" "}
              {new Date(subscription.data.currentPeriodEnd).toLocaleDateString("en-GB", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </CardDescription>
          </div>
          <div className="mb-6 mt-2 text-base">
            {upgrade && !isNonSeatResourceExhausted && (
              <>
                <br />
                <br />
                You must upgrade to the <strong className="capitalize">
                  {upgrade.newTier}
                </strong>{" "}
                plan, the <strong className="capitalize">{upgrade.oldTier}</strong> plan has a
                maximum of {upgrade.seatLimit} seats.
              </>
            )}
            {renderResourceUsage(resourceUsage, subscription.data.tier)}
          </div>
          {/* <PurchaseTasksButton teamId={teamId} teamSlug={teamSlug} successUrl={successUrl}>
            Buy more tasks
          </PurchaseTasksButton> */}
          <ManageSubscriptionButton
            teamId={teamId}
            teamSlug={teamSlug}
            stripeCustomerId={subscription.data.stripeCustomerId}
            successUrl={successUrl}
          >
            {renderManageSubscriptionText(
              isNonSeatResourceExhausted,
              subscription.data.tier,
              upgrade,
            )}
          </ManageSubscriptionButton>
        </CardContent>
      </Card>
    </div>
  )
}
